import styled, { css } from 'styled-components';
import { Radio, RadioIndicator, RadioLabel, Text } from '@lmn/design-system';
import { focusRing } from '@/app/components/search/styles/mixins';
import { createPriceByDisplayType } from '@/app/components/search/utils/formatting.ts';
import type { PRICE_DISPLAY_TYPE } from '@/app/components/search/utils/formattingTypes';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import { Icon } from '@/app/common/icons/Icon';
import { useTranslation } from '../../translations/Translation';

export type CancellationPolicyOption = {
  type: CancellationType;
  upTo?: string;
  deferredPaymentDepositPerMealPlan: DepositPerMealPlan[];
};

export type MealPlanTypeOption = {
  boardClass: BoardClassType;
  promotionKey: string | undefined;
  promotions: MealPlanPromotion[] | undefined;
};

type DepositPerMealPlan = {
  boardClass: BoardClassType;
  deferredPaymentDeposit: number | undefined;
  deferredPaymentDepositTotal: number | undefined;
};

export const DebugInfo = styled.div`
  display: flex;
  font-size: ${({ theme }) => theme.commons.font.sizes.small};
  font-weight: ${({ theme }) => theme.commons.font.weights.bold};
  line-height: ${({ theme }) => theme.commons.font.heights.small};
  justify-content: flex-end;
  word-break: break-word;
  text-align: right;
`;

export const MealPlanOption = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  margin: 0 -8px;
  padding: 8px;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  /* Accessibility: Use global focus ring for consistency */
  &:focus-visible {
    ${focusRing}
  }

  &[aria-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.5;
  }

  /* Suppress child focus styles to prevent double focus indicators */
  * {
    &:focus,
    &:focus-visible {
      outline: none !important;
      box-shadow: none !important;
    }
  }
`;

export const MealPlanOptionGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

export const MealPlanOptionGroupTitle = styled(Text).attrs({ variant: 'Caption', weight: 'bold' })`
  text-transform: uppercase;
  padding-bottom: 8px;
  display: flex;
  align-items: center;
`;

export const MealPlanOptionsGroups = styled.div<{ $hideBorder: boolean }>`
  display: flex;
  flex-direction: column;
  border: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderNeutral};
  border-radius: 8px;
  padding: 12px 16px;

  ${(p) =>
    p.$hideBorder
      ? `
   padding: 0;
   border: 0;`
      : ``}
`;

export const StyledRadio = styled(Radio)<{ $alignTop?: boolean }>`
  padding: 0;
  flex-grow: 1;
  pointer-events: none; /* Prevent double clicks - handle clicks at parent level */
  /* When the label wraps to several lines (meal plan + promotions), align the radio with the first
     line instead of centring it across the whole block, nudged down slightly to sit on the text. */
  ${({ $alignTop }) =>
    $alignTop &&
    css`
      align-items: flex-start;

      ${RadioIndicator} {
        margin-top: 1px;
      }
    `}

  ${RadioLabel} {
    font-size: ${(p) => p.theme.commons.font.sizes.base};
    line-height: ${(p) => p.theme.commons.font.heights.base};
    padding: 0;
    cursor: pointer;
  }

  /* Hide radio input and suppress focus styles */
  input[type='radio'] {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;

    &:focus {
      outline: none;
    }
  }

  /* Suppress label focus styles */
  label:focus {
    outline: none;
  }
`;

export function RoomsIncludedInPrice(props: { roomsIncludedInPrice: number }) {
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        background: ${({ theme }) => theme.kallisto.color.background.static.backgroundNeutralContrast};
        color: white;
        border-radius: 4px;
        font-weight: ${({ theme }) => theme.kallisto.font.weight.semibold};
        padding: 0 8px;
        height: 28px;
      `}
    >
      {/* MPH-1507: a count chip, not a heading; as an h5 it skipped levels below the h2 Room details title. */}
      <Text variant="Title5" color="textOnDark" forceTag="span">
        {`${props.roomsIncludedInPrice}x`}
      </Text>
    </div>
  );
}

export function getCancellationPolicy(cancellationPolicy: CancellationPolicyType) {
  return cancellationPolicy.type === 'REFUNDABLE'
    ? `${cancellationPolicy.type}-${cancellationPolicy.upTo}`
    : cancellationPolicy.type;
}

export function getRoomCancellationOptions(mealPlans: MealPlanType[] | readonly MealPlanType[]) {
  const cancellationPolicies: { [key: string]: CancellationPolicyOption } = {};

  mealPlans.forEach(({ boardClass, cancellationPolicy, deferredPaymentDeposit, deferredPaymentDepositTotal }) => {
    const cancellationKey = getCancellationPolicy(cancellationPolicy);
    const cancellationPolicyOption: CancellationPolicyOption = cancellationPolicies[cancellationKey] || {
      type: cancellationPolicy.type,
      upTo: cancellationPolicy.upTo,
      deferredPaymentDepositPerMealPlan: [],
    };
    cancellationPolicyOption.deferredPaymentDepositPerMealPlan = [
      ...cancellationPolicyOption.deferredPaymentDepositPerMealPlan,
      { boardClass, deferredPaymentDeposit, deferredPaymentDepositTotal },
    ];
    cancellationPolicies[cancellationKey] = cancellationPolicyOption;
  });

  return cancellationPolicies;
}

// Returns one backing meal plan per cancellation option the selected board class offers, so a cancellation
// radio group and its policy explainer stay in sync. Prefers the meal plan matching the currently selected
// bed and promotion (bed outranking promotion), falling back to any variant of the same board class —
// otherwise an option the board class genuinely offers would be hidden whenever the selected variant lacks
// that exact cancellation rate (rates carry their own bedId/promotionKey).
// `configurations` is price-sorted ascending, so among equally-close variants the cheapest wins.
export function getAvailableCancellationMealPlans(
  configurations: readonly MealPlanType[],
  selectedMealPlan: MealPlanType
): MealPlanType[] {
  const mealPlanByCancellationKey = new Map<string, MealPlanType>();

  // Switching cancellation type should keep everything else about the selection when possible: the bed is
  // part of the room the user chose, so it weighs more than keeping the same promotion rate.
  const matchScore = (mealPlan: MealPlanType) =>
    (mealPlan.bedId === selectedMealPlan.bedId ? 2 : 0) +
    (mealPlan.promotionKey === selectedMealPlan.promotionKey ? 1 : 0);

  configurations.forEach((mealPlan) => {
    if (mealPlan.boardClass !== selectedMealPlan.boardClass) return;

    const cancellationKey = getCancellationPolicy(mealPlan.cancellationPolicy);
    const existing = mealPlanByCancellationKey.get(cancellationKey);

    if (!existing || matchScore(mealPlan) > matchScore(existing)) {
      mealPlanByCancellationKey.set(cancellationKey, mealPlan);
    }
  });

  return [...mealPlanByCancellationKey.values()];
}

export function getRoomMealPlanTypeOptions(mealPlans: readonly MealPlanType[]) {
  const mealPlanTypes: { [key: string]: MealPlanTypeOption } = {};

  mealPlans.forEach(({ boardClass, promotionKey, promotions }) => {
    const mealPlanKey = `${boardClass}${promotionKey || ''}`;
    // `mealPlans` is price-sorted ascending and selecting an option resolves to its first matching
    // rate, so the first (cheapest) config must also be the one providing the option's promotion
    // labels — a later config in the same group may carry different label texts. The included meal
    // options are deliberately NOT taken from here: they describe what the guest actually gets, so
    // the option row reads them off the rate its own selection resolves to (see targetMealPlan).
    if (!mealPlanTypes[mealPlanKey]) {
      mealPlanTypes[mealPlanKey] = {
        boardClass,
        promotions,
        promotionKey,
      };
    }
  });

  return mealPlanTypes;
}

export function haveAllCancellationTypesAMealPlanType(
  allMealPlans: readonly MealPlanType[],
  cancellationTypes: CancellationPolicyOption[],
  mealPlanType: MealPlanTypeOption
) {
  const match = cancellationTypes?.filter((cancellationType) =>
    allMealPlans.find(
      (mealPlan) =>
        mealPlan.cancellationPolicy.type === cancellationType.type &&
        mealPlan.cancellationPolicy.upTo === cancellationType.upTo &&
        mealPlan.boardClass === mealPlanType.boardClass &&
        mealPlan.promotionKey === mealPlanType.promotionKey
    )
  );
  return match?.length === cancellationTypes?.length;
}

export function getPriceDifferenceByCancellationType(
  allMealPlans: MealPlanType[],
  cancellationType: CancellationType,
  upTo: string | undefined,
  localization: Localization
) {
  const {
    configValuesApp: { priceDisplayType },
  } = localization;
  const cheapestPrice = allMealPlans[0].finalPrice;
  const targetMealPlan = allMealPlans.find(
    (mealPlan) => mealPlan.cancellationPolicy.type === cancellationType && mealPlan.cancellationPolicy.upTo === upTo
  );
  // Calculate the difference in the same way as the display format
  const finalPriceVal = createPriceByDisplayType(targetMealPlan?.finalPrice || 0, priceDisplayType);
  const cheapestPriceVal = createPriceByDisplayType(cheapestPrice, priceDisplayType);
  return targetMealPlan?.finalPrice ? finalPriceVal - cheapestPriceVal : 0;
}

/**
 * The rate that choosing a meal plan option selects: the same cancellation policy and bed first, then the same bed,
 * then any rate of that meal plan and promotion. `mealPlans` is price-sorted ascending, so each step picks the
 * cheapest match.
 */
export function findMealPlanForOption(
  mealPlans: readonly MealPlanType[],
  mealPlanType: BoardClassType,
  promotionKey: string | undefined,
  bedId: string | undefined,
  cancellationPolicy: CancellationType,
  upTo: string | undefined
) {
  let targetMealPlan = mealPlans.find(
    (mealPlan) =>
      mealPlan.boardClass === mealPlanType &&
      mealPlan.promotionKey === promotionKey &&
      mealPlan.cancellationPolicy.type === cancellationPolicy &&
      mealPlan.cancellationPolicy.upTo === upTo &&
      mealPlan.bedId === bedId
  );

  if (!targetMealPlan) {
    targetMealPlan = mealPlans.find(
      (mealPlan) =>
        mealPlan.boardClass === mealPlanType && mealPlan.promotionKey === promotionKey && mealPlan.bedId === bedId
    );
  }

  if (!targetMealPlan) {
    targetMealPlan = mealPlans.find(
      (mealPlan) => mealPlan.boardClass === mealPlanType && mealPlan.promotionKey === promotionKey
    );
  }

  return targetMealPlan;
}

/**
 * MPH-1583: the fixed "+ X" difference of a meal plan option, computed with the selected cancellation policy and bed.
 * Every option is resolved to the rate that choosing it would select (see findMealPlanForOption), the baseline is
 * the cheapest of those resolved rates, and the result is this option's rate minus that baseline. Both prices are
 * rounded the way they are displayed first, so the label always equals the difference of the prices on screen. The
 * result is never negative, and it is 0 when the option has no rate.
 */
export function getMealPlanTypePriceDifference(
  allMealPlans: readonly MealPlanType[],
  mealPlanTypes: readonly MealPlanTypeOption[],
  mealPlanType: MealPlanTypeOption,
  selectedMealPlan: MealPlanType,
  priceDisplayType: PRICE_DISPLAY_TYPE
) {
  const resolvePrice = ({ boardClass, promotionKey }: MealPlanTypeOption) => {
    const targetMealPlan = findMealPlanForOption(
      allMealPlans,
      boardClass,
      promotionKey,
      selectedMealPlan.bedId,
      selectedMealPlan.cancellationPolicy.type,
      selectedMealPlan.cancellationPolicy.upTo
    );
    return targetMealPlan?.finalPrice
      ? createPriceByDisplayType(targetMealPlan.finalPrice, priceDisplayType)
      : undefined;
  };

  const targetPrice = resolvePrice(mealPlanType);
  if (targetPrice === undefined) return 0;

  const resolvedPrices = mealPlanTypes.map(resolvePrice).filter((price): price is number => price !== undefined);
  const baselinePrice = Math.min(targetPrice, ...resolvedPrices);

  return Math.max(0, targetPrice - baselinePrice);
}

export function getRoomBedTypesOptions(
  mealPlans: MealPlanType[] | readonly MealPlanType[],
  bedOptions: StaticRoomBedOption[] | undefined
) {
  const bedTypeOptions: { [key: string]: StaticRoomBedOption } = {};

  if (!bedOptions) return bedTypeOptions;

  mealPlans.forEach(({ bedId }) => {
    const bedTypeOption = bedOptions.find((bedOption) => bedOption.code === bedId);
    if (bedTypeOption) {
      bedTypeOptions[bedId] = bedTypeOption;
    }
  });

  return bedTypeOptions;
}

export function IncludedRoomTitle() {
  const { t } = useTranslation();

  return (
    <div
      css={css`
        display: flex;
        gap: 4px;
        align-items: center;
        padding: 0 8px;
        background-color: ${(p) => p.theme.commons.colors.primary400};
        color: white;
        text-transform: uppercase;
      `}
    >
      {}
      <Icon category="Semantics" name="icon-check" size={16} color="iconOnDark" alt="Included Room" />
      <Text variant="Title6">{t('vacation.select.room_card.included_room')}</Text>
    </div>
  );
}
